import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Input, Label, cn } from '@zafroi/ui-kit';
import { Eye, Info, Loader2, Monitor, PlusCircle, Save, Smartphone } from 'lucide-react';
import {
  listEmailTemplates,
  previewTransactionalEmail,
  saveTransactionalEmail,
} from '@/services/engage-api';
import { ApiError } from '@/services/api-client';
import type { TransactionalEmailTemplate } from '@/types/engage';
import { NotificationRecipientsPanel } from './NotificationRecipientsPanel';

interface Draft {
  subject: string;
  bodyCopy: string;
  enabled: boolean;
}

/**
 * Tenant-level transactional email templates (invoice, etc.) — editable copy +
 * a LIVE branded preview rendered by the backend (the same shell that ships to
 * customers). Branding (logo/colour) is set in the workspace Organization
 * settings; here you edit the message copy.
 */
export function TransactionalEmailTemplatesPage() {
  const queryClient = useQueryClient();
  const { data: templates, isLoading, isError } = useQuery({
    queryKey: ['transactional-email-templates'],
    queryFn: listEmailTemplates,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Seed editable drafts + select the first template once loaded.
  useEffect(() => {
    if (!templates) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const t of templates) {
        if (!next[t.key])
          next[t.key] = { subject: t.subject, bodyCopy: t.bodyCopy, enabled: t.enabled };
      }
      return next;
    });
    setSelectedKey((k) => k ?? templates[0]?.key ?? null);
  }, [templates]);

  const selected = useMemo(
    () => templates?.find((t) => t.key === selectedKey) ?? null,
    [templates, selectedKey],
  );
  const draft = selectedKey ? drafts[selectedKey] : undefined;

  // Debounced live preview — the backend renders the actual branded email.
  useEffect(() => {
    if (!selectedKey || !draft) return;
    setPreviewing(true);
    const handle = setTimeout(() => {
      previewTransactionalEmail(selectedKey, {
        subject: draft.subject,
        bodyCopy: draft.bodyCopy,
        mode: 'THEMED',
      })
        .then((r) => {
          setPreviewHtml(r.html);
          setPreviewSubject(r.subject);
        })
        .catch(() => {
          /* preview is best-effort */
        })
        .finally(() => setPreviewing(false));
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, draft?.subject, draft?.bodyCopy]);

  function patch(p: Partial<Draft>) {
    if (!selectedKey) return;
    setDrafts((prev) => ({ ...prev, [selectedKey]: { ...prev[selectedKey], ...p } }));
  }

  function insertToken(token: string) {
    if (!selectedKey || !draft) return;
    const el = bodyRef.current;
    if (!el) {
      patch({ bodyCopy: draft.bodyCopy + token });
      return;
    }
    const start = el.selectionStart ?? draft.bodyCopy.length;
    const end = el.selectionEnd ?? draft.bodyCopy.length;
    patch({ bodyCopy: draft.bodyCopy.slice(0, start) + token + draft.bodyCopy.slice(end) });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  const save = useMutation({
    mutationFn: () => {
      if (!selectedKey || !draft) throw new Error('No template selected');
      return saveTransactionalEmail(selectedKey, {
        subject: draft.subject,
        bodyCopy: draft.bodyCopy,
        enabled: draft.enabled,
        mode: 'THEMED',
      });
    },
    onSuccess: () => {
      toast.success('Template saved');
      queryClient.invalidateQueries({ queryKey: ['transactional-email-templates'] });
    },
    onError: (err) =>
      toast.error('Could not save template', {
        description: err instanceof ApiError ? err.message : String(err),
      }),
  });

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !templates) {
    return (
      <div className="mx-auto max-w-md py-16 text-center text-sm text-muted-foreground">
        Couldn't load your email templates.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Email templates</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Edit the transactional emails your customers receive. The preview is exactly what
          gets sent — wrapped in your brand (set the logo &amp; colour in your workspace
          Organization settings).
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* ── Template list ── */}
        <aside className="space-y-2">
          {templates.map((t) => (
            <TemplateRow
              key={t.key}
              t={t}
              active={t.key === selectedKey}
              onClick={() => setSelectedKey(t.key)}
            />
          ))}
        </aside>

        {/* ── Editor + preview ── */}
        {selected && draft ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Editor */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <ToggleChip
                  on={draft.enabled}
                  onClick={() => patch({ enabled: !draft.enabled })}
                  label={draft.enabled ? 'Enabled' : 'Disabled'}
                />
                <span className="text-[11px] text-muted-foreground">{selected.description}</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tpl-subject" className="text-xs">
                  Subject
                </Label>
                <Input
                  id="tpl-subject"
                  value={draft.subject}
                  onChange={(e) => patch({ subject: e.target.value })}
                  className="font-mono text-xs"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tpl-body" className="text-xs">
                  Message
                </Label>
                <textarea
                  id="tpl-body"
                  ref={bodyRef}
                  value={draft.bodyCopy}
                  onChange={(e) => patch({ bodyCopy: e.target.value })}
                  rows={14}
                  className="min-h-72 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-xs leading-relaxed shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground">
                  Plain text — we wrap it in your branded shell automatically. Blank lines
                  start new paragraphs.
                </p>
              </div>

              {/* Token palette */}
              {selected.tokens.length > 0 && (
                <div className="rounded-md border border-border bg-muted/40 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Info className="h-3 w-3" />
                    Click to insert a merge field at the cursor
                  </p>
                  <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {selected.tokens.map((p) => (
                      <li key={p.token} className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => insertToken(p.token)}
                          className="inline-flex shrink-0 items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <PlusCircle className="h-2.5 w-2.5" />
                          {p.token}
                        </button>
                        <span className="text-[11px] leading-relaxed text-muted-foreground">
                          {p.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save template
                </Button>
              </div>
            </div>

            {/* Live preview */}
            <div className="lg:sticky lg:top-4 lg:self-start">
              <div className="mb-2 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Eye className="h-3 w-3" />
                  Live preview
                  {previewing && <Loader2 className="h-3 w-3 animate-spin" />}
                </p>
                <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
                  <DeviceButton active={device === 'desktop'} onClick={() => setDevice('desktop')}>
                    <Monitor className="h-3.5 w-3.5" />
                  </DeviceButton>
                  <DeviceButton active={device === 'mobile'} onClick={() => setDevice('mobile')}>
                    <Smartphone className="h-3.5 w-3.5" />
                  </DeviceButton>
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border border-border bg-muted/30 shadow-elev-1">
                <div className="border-b border-border bg-background px-4 py-2.5 text-xs">
                  <span className="text-muted-foreground">Subject:&nbsp;</span>
                  <span className="font-medium text-foreground">{previewSubject || '—'}</span>
                </div>
                <div className="flex justify-center p-4">
                  <iframe
                    title="Email preview"
                    sandbox=""
                    srcDoc={previewHtml}
                    className={cn(
                      'h-[640px] w-full rounded border border-border bg-white transition-all',
                      device === 'mobile' ? 'max-w-[390px]' : 'max-w-[640px]',
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Select a template to edit.
          </div>
        )}
      </div>

      <div className="mt-6">
        <NotificationRecipientsPanel />
      </div>
    </div>
  );
}

function TemplateRow({
  t,
  active,
  onClick,
}: {
  t: TransactionalEmailTemplate;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-md border p-3 text-left transition-colors',
        active ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-accent/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{t.label}</span>
        <Badge tone={t.isCustomized ? 'success' : 'neutral'} withDot={false} className="text-[10px]">
          {t.isCustomized ? 'Customized' : 'Default'}
        </Badge>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{t.description}</p>
    </button>
  );
}

function DeviceButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded p-1 transition-colors',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ToggleChip({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        on
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-border bg-muted text-muted-foreground',
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', on ? 'bg-primary' : 'bg-muted-foreground/50')} />
      {label}
    </button>
  );
}
